// React host for the shop's live 3D item preview (three/itemPreview.ts).
// The 3D code loads on demand; until then, or without WebGL, the item's
// picture shows instead. Small +/- buttons zoom (as do the wheel and a pinch).
import { useEffect, useRef, useState } from "react";
import type { ItemDef, WizardLook } from "@wp/shared";
import type { ItemPreview } from "../../three/itemPreview";

/** Same range as three/itemPreview.ts (kept here so the 3D code still loads on demand). */
const MIN_ZOOM = 0.45;
const MAX_ZOOM = 1.8;
/** One button press: 20% closer or further. */
const ZOOM_STEP = 1.25;
import { ItemThumb } from "./ItemThumb";

interface Props {
  item: ItemDef;
  /** What your wizard wears otherwise. */
  look: WizardLook;
  animate: boolean;
}

export function ItemPreviewView({ item, look, animate }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const preview = useRef<ItemPreview | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  const [zoom, setZoom] = useState(1.05);
  const latest = useRef({ item, look, animate });
  latest.current = { item, look, animate };

  useEffect(() => {
    let cancelled = false;
    let made: ItemPreview | null = null;
    void import("../../three/itemPreview")
      .then(({ ItemPreview }) => {
        const host = hostRef.current;
        if (cancelled || !host) return;
        made = new ItemPreview(host, latest.current.animate);
        made.onZoom = setZoom;
        made.show(latest.current.item, latest.current.look);
        setZoom(made.getZoom());
        preview.current = made;
        setState("ready");
      })
      .catch(() => !cancelled && setState("failed"));
    return () => {
      cancelled = true;
      preview.current = null;
      made?.dispose();
    };
  }, []);

  useEffect(() => preview.current?.show(item, look), [item, look]);
  useEffect(() => preview.current?.setAnimate(animate), [animate]);

  return (
    <div className="item-preview-wrap">
      <div ref={hostRef} className="item-preview" data-testid="item-preview" aria-label={`${item.name} preview`} role="img">
        {state !== "ready" && <ItemThumb item={item} size={160} />}
      </div>
      {state === "ready" && (
        <div className="item-preview-zoom">
          <button type="button" aria-label="Zoom in" title="Zoom in" disabled={zoom <= MIN_ZOOM + 1e-3} onClick={() => preview.current?.zoomBy(1 / ZOOM_STEP)}>
            +
          </button>
          <button type="button" aria-label="Zoom out" title="Zoom out" disabled={zoom >= MAX_ZOOM - 1e-3} onClick={() => preview.current?.zoomBy(ZOOM_STEP)}>
            −
          </button>
        </div>
      )}
    </div>
  );
}
